import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

interface Product {
  name: string;
  image: string;
  note: string;
}

interface Category {
  emoji: string;
  label: string;
  color: string;
  bgColor: string;
  products: Product[];
}

const categories: Category[] = [
  {
    emoji: '🧴',
    label: 'Skincare',
    color: '#D4A89A',
    bgColor: '#FDF5F2',
    products: [
      {
        name: 'Sérum Vitamina C',
        image: 'https://public.readdy.ai/ai/img_res/332f19ee6f2c24df4104a4a2816832d5.jpg',
        note: 'Mi básico de mañana',
      },
      {
        name: 'Crema hidratante',
        image: 'https://readdy.ai/api/search-image?query=elegant%20moisturizer%20cream%20jar%20on%20clean%20white%20surface%20with%20soft%20natural%20light%2C%20minimal%20beauty%20product%20in%20soft%20pink%20packaging%2C%20luxury%20skincare%20container%2C%20clean%20product%20photography%2C%20warm%20golden%20tones%2C%20contemporary%20feminine%20aesthetic%2C%20simple%20background&width=400&height=400&seq=cat-skincare-002&orientation=squarish',
        note: 'Piel seca salvada',
      },
    ],
  },
  {
    emoji: '💄',
    label: 'Belleza',
    color: '#C4899A',
    bgColor: '#FDF2F5',
    products: [
      {
        name: 'Labial nude natural',
        image: 'https://readdy.ai/api/search-image?query=minimal%20nude%20lipstick%20on%20clean%20white%20surface%20with%20soft%20natural%20light%2C%20elegant%20beauty%20product%20photography%2C%20warm%20beige%20and%20rose%20tones%2C%20simple%20clean%20aesthetic%2C%20contemporary%20makeup%20packaging%2C%20soft%20diffused%20shadows%2C%20high%20end%20cosmetic%20product&width=400&height=400&seq=cat-beauty-001&orientation=squarish',
        note: 'Color perfecto para diario',
      },
      {
        name: 'Máscara de pestañas',
        image: 'https://readdy.ai/api/search-image?query=elegant%20mascara%20tube%20on%20clean%20white%20marble%20surface%20with%20soft%20natural%20morning%20light%2C%20minimal%20beauty%20product%20photography%2C%20warm%20beige%20tones%2C%20simple%20clean%20aesthetic%2C%20contemporary%20makeup%20packaging%2C%20soft%20diffused%20shadows%2C%20high%20end%20cosmetic%20product&width=400&height=400&seq=cat-beauty-002&orientation=squarish',
        note: 'Sin grumos, lo prometo',
      },
    ],
  },
  {
    emoji: '🏠',
    label: 'Hogar',
    color: '#8B9E82',
    bgColor: '#F4F7F2',
    products: [
      {
        name: 'Vela de soja aromática',
        image: 'https://readdy.ai/api/search-image?query=minimal%20soy%20candle%20in%20ceramic%20jar%20on%20clean%20wooden%20surface%20with%20soft%20natural%20afternoon%20light%2C%20cozy%20home%20product%20photography%2C%20warm%20beige%20and%20cream%20tones%2C%20simple%20clean%20aesthetic%2C%20contemporary%20home%20decor%2C%20soft%20diffused%20shadows%2C%20artisan%20handmade%20candle&width=400&height=400&seq=cat-home-001&orientation=squarish',
        note: 'Huele increíble',
      },
      {
        name: 'Toallas de lino',
        image: 'https://readdy.ai/api/search-image?query=folded%20natural%20linen%20towels%20on%20clean%20white%20surface%20with%20soft%20natural%20light%2C%20minimal%20home%20textile%20product%20photography%2C%20warm%20beige%20and%20cream%20tones%2C%20simple%20clean%20aesthetic%2C%20contemporary%20home%20decor%2C%20soft%20diffused%20shadows%2C%20organic%20cotton%20fabric&width=400&height=400&seq=cat-home-002&orientation=squarish',
        note: 'Suaves y duraderas',
      },
    ],
  },
];

export default function CategoriesSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!('IntersectionObserver' in window)) {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setInView(true);
        }
      },
      { threshold: 0.15 },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative bg-white py-16 lg:py-20 px-8 md:px-12 lg:px-[8%] overflow-hidden"
    >
      <div className="relative z-10">
        <div
          className={`mb-10 transition-all duration-700 ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <p className="text-[13px] tracking-[0.12em] text-[#A39B92] font-normal mb-3">
            Explora por categoría
          </p>
          <h2 className="text-[clamp(1.8rem,3.5vw,2.6rem)] font-bold text-[#222222] leading-tight tracking-[-0.01em]">
            Lo que recomiendo, organizado ✨
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8" data-product-shop>
          {categories.map((cat, catIndex) => (
            <Link
              key={cat.label}
              to="/explorar"
              className={`group rounded-lg overflow-hidden transition-all duration-700 hover:shadow-md hover:-translate-y-1 cursor-pointer ${
                inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              }`}
              style={{
                transitionDelay: `${200 + catIndex * 150}ms`,
                backgroundColor: cat.bgColor,
              }}
            >
              {/* Cabecera de categoría */}
              <div className="px-6 pt-6 pb-4">
                <div className="flex items-center gap-3 mb-1">
                  <span className="text-[28px]">{cat.emoji}</span>
                  <h3 className="text-[20px] font-semibold text-[#222222]">
                    {cat.label}
                  </h3>
                </div>
              </div>

              {/* Productos */}
              <div className="px-6 pb-6 flex flex-col gap-4">
                {cat.products.map((product) => (
                  <div
                    key={product.name}
                    className="bg-white rounded-lg p-3 flex items-center gap-4 hover:shadow-sm transition-all duration-300"
                  >
                    <div className="w-[72px] h-[72px] flex-shrink-0 rounded-lg overflow-hidden">
                      <img
                        src={product.image}
                        alt={product.name}
                        title={`${product.name} - LunaFinds`}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[15px] font-medium text-[#222222] truncate">
                        {product.name}
                      </p>
                      <p className="text-[13px] text-[#6B6860] font-light mt-0.5">
                        {product.note}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Ver más */}
              <div className="px-6 pb-6">
                <div
                  className="flex items-center gap-2 text-[13px] font-medium group-hover:gap-3 transition-all duration-300"
                  style={{ color: cat.color }}
                >
                  <span>Ver todo {cat.label.toLowerCase()}</span>
                  <i className="ri-arrow-right-line text-[14px]"></i>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
